import React, { useState, useRef, useEffect, } from "react";
import TextField from "./TextField";
import cn from "./helpers/cn";

// Base de propriedades comuns que não dependem do 'multiple'
export type BaseAutocompleteProps<T> = {
  options: T[];
  label?: string;
  placeholder?: string;
  onInputChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  getOptionLabel: (option: T) => string;
  getOptionDisabled?: (option: T) => boolean;
  renderOption?: (option: T, state: { selected: boolean }) => React.ReactNode;
  noOptionsText?: React.ReactNode;
  endAdornment?: React.ReactNode;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  className?: string;
  disabled?: boolean;
  size?: "small" | "medium";
};

// Tipo Condicional: Inspeciona a flag "Multiple" para decidir as assinaturas corretas
export type AutocompleteProps<T, Multiple extends boolean | undefined = undefined> = BaseAutocompleteProps<T> &
  (Multiple extends true
    ? {
        multiple: true;
        value?: T[] | null;
        defaultValue?: T[] | null;
        onChange?: (options: T[]) => void;
      }
    : {
        multiple?: Multiple; // Aceita false ou undefined
        value?: T | null;
        defaultValue?: T | null;
        onChange?: (option: T) => void;
      });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Autocomplete<T = any, Multiple extends boolean | undefined = undefined>({
  options,
  label,
  placeholder,
  value,
  defaultValue,
  multiple,
  onChange,
  onInputChange,
  onBlur,
  getOptionLabel,
  getOptionDisabled,
  renderOption,
  noOptionsText = "Sem resultados",
  endAdornment,
  fullWidth,
  error,
  helperText,
  className,
  disabled,
  size,
}: AutocompleteProps<T, Multiple>): JSX.Element {
  // Em modo multiple o value é um array; nos demais casos é uma única opção.
  // O defaultValue serve como fallback inicial quando value não é informado.
  const selectedValues: T[] = multiple
    ? ((value as T[] | undefined | null) ?? (defaultValue as T[] | undefined | null) ?? [])
    : [];
  const singleValue = multiple
    ? null
    : ((value as T | null | undefined) ?? (defaultValue as T | null | undefined));

  // onChange tem assinatura distinta por modo; referências tipadas internamente para facilitar a chamada.
  const emitSingle = onChange as ((option: T) => void) | undefined;
  const emitMultiple = onChange as ((options: T[]) => void) | undefined;

  // getOptionLabel pode devolver undefined quando o registro não traz o campo usado
  // como rótulo (ex.: medicalLocations sem `name`). Sem esta proteção o filtro
  // estoura a cada tecla digitada e o campo não pesquisa nada.
  const labelOf = (option: T): string => getOptionLabel(option) ?? "";

  const [inputValue, setInputValue] = useState(
    !multiple && singleValue ? labelOf(singleValue) : ""
  );
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<T[]>(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  // Rótulo da opção selecionada (modo single). Usado como dependência do efeito
  // abaixo para evitar que o input seja resetado a cada tecla digitada — o
  // getOptionLabel costuma ser uma função inline (nova referência a cada render).
  const singleLabel = !multiple && singleValue ? labelOf(singleValue) : "";

  useEffect(() => {
    if (!multiple && singleValue) {
      setInputValue(labelOf(singleValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleLabel, multiple]);

  const isOptionSelected = (option: T) =>
    multiple
      ? selectedValues.some((v) => labelOf(v) === labelOf(option))
      : singleValue
        ? labelOf(singleValue) === labelOf(option)
        : false;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const val = e.target.value;

    setInputValue(val);

    if (onInputChange) {
      onInputChange(val);
    }

    setFilteredOptions(
      options.filter((opt) =>
        labelOf(opt).toLowerCase().includes(val.toLowerCase())
      )
    );
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  const handleSelectOption = (option: T) => {
    if (disabled) return;

    if (multiple) {
      const alreadySelected = isOptionSelected(option);
      const next = alreadySelected
        ? selectedValues.filter((v) => labelOf(v) !== labelOf(option))
        : [...selectedValues, option];

      // Limpa o filtro de digitação e mantém o dropdown aberto para novas seleções
      setInputValue("");
      setFilteredOptions(options);
      emitMultiple?.(next);

      return;
    }

    setInputValue(labelOf(option));
    setIsOpen(false);
    emitSingle?.(option);
  };

  const handleClear = () => {
    if (disabled) return;

    setInputValue("");
    setFilteredOptions(options);
    setHighlightedIndex(-1);

    if (multiple) {
      emitMultiple?.([]);
    } else {
      // Limpa a seleção única; o componente pai trata o valor nulo no onChange.
      emitSingle?.(null as unknown as T);
    }
  };

  const showClear = !disabled && (multiple ? selectedValues.length > 0 : !!inputValue);

  const handleRemoveChip = (option: T) => {
    if (disabled) return;

    const next = selectedValues.filter(
      (v) => labelOf(v) !== labelOf(option)
    );

    emitMultiple?.(next);
  };

  return (
    <div className={cn("relative inline-block", fullWidth && "w-full")} ref={containerRef}>
      <TextField
        className={className}
        label={label}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          if (disabled) return;
          setFilteredOptions(options);
          setIsOpen(true);
        }}
        onBlur={onBlur}
        fullWidth={fullWidth}
        error={error}
        helperText={helperText}
        disabled={disabled}
        size={size}
        autoComplete="off"
        placeholder={multiple && selectedValues.length > 0 ? "" : placeholder}
        InputProps={{
          startAdornment: multiple && selectedValues.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1 mr-1">
              {selectedValues.map((item, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700"
                >
                  {labelOf(item)}
                  {!disabled && (
                    <button
                      type="button"
                      className="text-slate-400 hover:text-slate-700"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemoveChip(item);
                      }}
                    >
                      <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </span>
              ))}
            </div>
          ) : undefined,
          endAdornment: (
            <div className="flex items-center">
              {endAdornment}
              {showClear && (
                <button
                  type="button"
                  aria-label="Limpar seleção"
                  className="text-slate-400 hover:text-slate-700 mr-0.5"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClear();
                  }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              <svg className={cn("w-5 h-5 transition-transform text-slate-400", isOpen && "rotate-180")} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          )
        }}
      />

      {!disabled && isOpen && (
        <ul className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded shadow-xl max-h-60 overflow-auto py-1">
          {filteredOptions.length === 0 ? (
            <li className="px-4 py-2 text-sm text-slate-500">
              {noOptionsText}
            </li>
          ) : (
            filteredOptions.map((option, index) => {
              const isSelected = isOptionSelected(option);
              const isOptionDisabled = getOptionDisabled ? getOptionDisabled(option) : false;

              return (
                <li
                  key={index}
                  onClick={() => !isOptionDisabled && handleSelectOption(option)}
                  aria-disabled={isOptionDisabled}
                  className={cn(
                    "px-4 py-2 text-sm transition-colors flex items-center justify-between gap-2",
                    isOptionDisabled
                      ? "cursor-default opacity-50 pointer-events-none"
                      : "cursor-pointer",
                    !isOptionDisabled && (index === highlightedIndex ? "bg-slate-100" : "hover:bg-slate-50"),
                    isSelected ? "text-blue-600 font-semibold" : "text-slate-900"
                  )}
                  onMouseEnter={() => !isOptionDisabled && setHighlightedIndex(index)}
                >
                  {renderOption ? renderOption(option, { selected: isSelected }) : labelOf(option)}
                  {multiple && isSelected && (
                    <svg className="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

export default Autocomplete;